import { useEffect } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreRequired } from 'features/store/hooks/use-store-required';

import { CardModal } from 'components/card-modal';
import { LocationEmptyState } from 'components/location-empty-state';
import { toaster } from 'components/toaster/toaster';
import IncentiveDetails from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useGetCmsAndLoyaltyOffers } from 'pages/loyalty/loyalty-offers/use-get-cms-and-loyalty-offers';
import { useGetIncentive } from 'pages/loyalty/loyalty-offers/use-get-incentive';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LoyaltyEngineId } from 'state/loyalty/types';

export default function OffersDetail() {
  const { noStoreSelected } = useStoreRequired();

  const router = useRouter();

  const { loyaltyEngineId } = useLocalSearchParams<{ loyaltyEngineId: LoyaltyEngineId }>();

  const { selectedIncentive, isIncentivesLoading } = useGetIncentive({ loyaltyEngineId });

  const incentiveExists = !!selectedIncentive;

  // only runs on offer deep link and you never visited parent offer screen
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const { refreshOffers } = useGetCmsAndLoyaltyOffers();
  useEffect(() => {
    if (shouldRefetchOffers) {
      refreshOffers();
    }
  }, [refreshOffers, shouldRefetchOffers]);
  // close with error toast if incentive can't be found
  useEffect(() => {
    if (!isIncentivesLoading && !incentiveExists) {
      if (!router.canDismiss()) {
        return; // nav is loading still on a deep link
      }
      router.dismiss();
      toaster.negative(t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incentiveExists, isIncentivesLoading]);

  if (noStoreSelected) {
    return (
      <CardModal alwaysShowHeader>
        <LocationEmptyState title={t('emptyOffersHeading')} message={t('emptyMenuMessage')} />
      </CardModal>
    );
  }
  // if the incentive doesn't exist above will dismiss
  if (isIncentivesLoading || !incentiveExists) {
    return (
      <CardModal alwaysShowHeader isLoading>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return (
    <IncentiveDetails
      engineIncentivesMap={{}}
      selectedIncentive={selectedIncentive}
      showUnavailableDefaultMessage={selectedIncentive?.isUnavailable}
    />
  );
}
